/** Stable keys for filters and i18n display (Credit Union Blueprint). */

export const TIER_KEYS = { 'Top Tier': 'top_tier', Strong: 'strong', Good: 'good' };
export const CONFIDENCE_KEYS = { High: 'high', Medium: 'medium' };
export const RESTRICTION_KEYS = {
  Nationwide: 'nationwide',
  Regional: 'regional',
  'Occupation-Based': 'occupation_based',
};
export const ASSOC_TYPE_KEYS = {
  'Financial Education': 'financial_education',
  Charitable: 'charitable',
  'Historical Society': 'historical_society',
  'Space/Science': 'space_science',
  'Free Subscription': 'free_subscription',
  'Recreation/Parks': 'recreation_parks',
  Other: 'other',
};

const BEST_FOR_KEYS = {
  'General banking': 'general_banking',
  'High-limit credit products': 'high_limit_products',
  'Auto loans': 'auto_loans',
  'Credit building': 'credit_building',
  'Business accounts': 'business_accounts',
  'Balance transfers': 'balance_transfers',
  'General membership': 'general_membership',
  'Nationwide access via ACC': 'nationwide_acc',
  'High-limit products': 'high_limit_products',
};

const WHY_KEYS = {
  'Established institution with strong product offerings': 'established_institution',
  'Solid credit union with accessible membership': 'solid_membership',
  'ACC membership provides alternative entry path': 'acc_entry',
  'Association membership provides nationwide access': 'association_access',
};

const GUIDANCE_KEYS = {
  'Open with minimum deposit': 'open_deposit',
  'Build relationship before requesting products': 'build_relationship',
  'Direct deposit recommended': 'direct_deposit',
  'Verify ACC acceptance before applying': 'verify_acc',
  'ACC membership may have annual fee': 'acc_fee',
  'Some CUs waive ACC fee': 'acc_waive',
  'Join association during CU application': 'join_during_app',
  'Association fee is usually one-time': 'association_onetime',
  'Verify current pricing': 'verify_pricing',
};

export const tierClassFromKey = (tierKey) =>
  (tierKey || 'good').replace(/_/g, '-');

export const label = (t, CU, path, fallback = '') =>
  t(`${CU}.${path}`, null, fallback);

export const labelTier = (t, CU, tierKey) =>
  label(t, CU, `labels.tiers.${tierKey}`, tierKey);
export const labelConfidence = (t, CU, key) =>
  label(t, CU, `labels.confidence.${key}`, key);
export const labelRestriction = (t, CU, key) =>
  label(t, CU, `labels.restrictions.${key}`, key);
export const labelAssocType = (t, CU, key) =>
  label(t, CU, `labels.associationTypes.${key}`, key);
export const labelBestFor = (t, CU, key) =>
  label(t, CU, `labels.bestFor.${key}`, key);
export const labelWhy = (t, CU, key) =>
  label(t, CU, `labels.whyItMatters.${key}`, key);
export const labelGuidance = (t, CU, key) =>
  label(t, CU, `labels.guidance.${key}`, key);

const mapKey = (map, value, defaultKey) => map[value] || defaultKey;

const fallbackNotes = (t, CU, rowKey, raw, isFallback) => {
  if (!isFallback || !rowKey) return raw;
  return t(`${CU}.fallback.notes.${rowKey}`, null, raw);
};

const fallbackHowToJoin = (t, CU, rowKey, raw, isFallback) => {
  if (!isFallback || !rowKey) return raw;
  return t(`${CU}.fallback.howToJoin.${rowKey}`, null, raw);
};

export const transformOpenToEveryoneRow = (row, t, CU, isFallback) => {
  const data = row.row_data || {};
  const rowKey = row.id || row.row_key;
  const name = data.credit_union || '';
  const notesRaw = data.notes || data.notes_highlights || '';
  const notesLower = notesRaw.toLowerCase();
  const howToJoin = fallbackHowToJoin(t, CU, rowKey, data.how_to_join || '$5 deposit', isFallback);
  const minDeposit = data.deposit_required
    || (howToJoin.match(/\$\d+/)?.[0] ?? '$5');
  const notes = fallbackNotes(t, CU, rowKey, notesRaw, isFallback);

  let tier = data.tier || 'Good';
  if (!data.tier) {
    if (name.includes('PenFed') || name.includes('Alliant') || name.includes('Lake Michigan')
      || name.includes('Consumers') || name.includes('America First')) {
      tier = 'Top Tier';
    } else if (notesLower.includes('high-limit') || notesLower.includes('cdfi')
      || notesLower.includes('100%') || notesLower.includes('excellent')) {
      tier = 'Strong';
    }
  }
  const tierKey = mapKey(TIER_KEYS, tier, 'good');

  let confidence = 'Medium';
  if (notesLower.includes('excellent') || notesLower.includes('great')
    || notesLower.includes('strong') || notesLower.includes('known for')) {
    confidence = 'High';
  }
  const confidenceKey = mapKey(CONFIDENCE_KEYS, confidence, 'medium');

  const recommended = tierKey === 'top_tier' || (tierKey === 'strong' && notesLower.includes('cdfi'));

  let bestFor = 'General banking';
  if (notesLower.includes('high-limit')) bestFor = 'High-limit credit products';
  else if (notesLower.includes('auto')) bestFor = 'Auto loans';
  else if (notesLower.includes('credit builder') || notesLower.includes('secured')) bestFor = 'Credit building';
  else if (notesLower.includes('business')) bestFor = 'Business accounts';
  else if (notesLower.includes('balance transfer')) bestFor = 'Balance transfers';
  const bestForKey = mapKey(BEST_FOR_KEYS, bestFor, 'general_banking');

  const whyItMattersKey = tierKey === 'top_tier' ? 'established_institution' : 'solid_membership';
  const guidanceKeys = ['open_deposit', 'build_relationship', 'direct_deposit'];

  return {
    id: rowKey,
    name,
    howToJoin,
    minDeposit,
    notes,
    tierKey,
    tierClass: tierClassFromKey(tierKey),
    tierLabel: labelTier(t, CU, tierKey),
    confidenceKey,
    confidenceLabel: labelConfidence(t, CU, confidenceKey),
    recommended,
    bestForKey,
    bestForLabel: labelBestFor(t, CU, bestForKey),
    whyItMattersKey,
    whyItMattersLabel: labelWhy(t, CU, whyItMattersKey),
    guidanceKeys,
    guidanceLabels: guidanceKeys.map((k) => labelGuidance(t, CU, k)),
  };
};

export const transformACCVerifiedRow = (row, t, CU, isFallback) => {
  const data = row.row_data || {};
  const rowKey = row.id || row.row_key;
  const name = data.credit_union || '';
  const stateRestriction = data.state_restriction || 'None';
  const notesRaw = data.notes || data.notes_highlights || '';
  const notesLower = notesRaw.toLowerCase();
  const restrictionIsOpen = !stateRestriction || /^none\b/i.test(stateRestriction.trim());
  const notes = fallbackNotes(t, CU, rowKey, notesRaw, isFallback);

  let restrictionGroupKey = 'nationwide';
  if (!restrictionIsOpen) {
    if (stateRestriction.includes('Federal') || stateRestriction.includes('employees')) {
      restrictionGroupKey = 'occupation_based';
    } else {
      restrictionGroupKey = 'regional';
    }
  }

  let confidence = 'Medium';
  if (notesLower.includes('nationwide') || notesLower.includes('waives') || name.includes('NASA')
    || name.includes('Alliant') || name.includes('First Tech') || name.includes('SDFCU')) {
    confidence = 'High';
  }
  const confidenceKey = mapKey(CONFIDENCE_KEYS, confidence, 'medium');
  const recommended = confidenceKey === 'high' && restrictionGroupKey === 'nationwide';

  const bestForKey = restrictionGroupKey === 'nationwide' ? 'nationwide_acc' : 'general_banking';
  const whyItMattersKey = 'acc_entry';
  const guidanceKeys = ['verify_acc', 'acc_fee', 'acc_waive'];

  return {
    id: rowKey,
    name,
    accAccess: data.acc_access || 'Yes',
    stateRestriction,
    notes,
    confidenceKey,
    confidenceLabel: labelConfidence(t, CU, confidenceKey),
    recommended,
    restrictionGroupKey,
    restrictionGroupLabel: labelRestriction(t, CU, restrictionGroupKey),
    bestForKey,
    bestForLabel: restrictionGroupKey === 'nationwide'
      ? labelBestFor(t, CU, 'nationwide_acc')
      : `${stateRestriction} / ACC`,
    whyItMattersKey,
    whyItMattersLabel: labelWhy(t, CU, whyItMattersKey),
    guidanceKeys,
    guidanceLabels: guidanceKeys.map((k) => labelGuidance(t, CU, k)),
  };
};

export const transformAssociationRoutesRow = (row, t, CU, isFallback) => {
  const data = row.row_data || {};
  const rowKey = row.id || row.row_key;
  const name = data.credit_union || '';
  const association = data.association || data.association_name || data.how_to_join || '';
  const cost = data.cost || '';
  const notesRaw = data.notes || data.notes_highlights || '';
  const notesLower = notesRaw.toLowerCase();
  const notes = fallbackNotes(t, CU, rowKey, notesRaw, isFallback);

  let associationTypeKey = 'other';
  const assocLower = association.toLowerCase();
  if (assocLower.includes('financial fitness') || assocLower.includes('financial education')
    || assocLower.includes('coalition for financial')) {
    associationTypeKey = 'financial_education';
  } else if (assocLower.includes('foster care') || assocLower.includes('success')) {
    associationTypeKey = 'charitable';
  } else if (assocLower.includes('historical') || assocLower.includes('history')) {
    associationTypeKey = 'historical_society';
  } else if (assocLower.includes('space') || assocLower.includes('nasa') || assocLower.includes('nss')) {
    associationTypeKey = 'space_science';
  } else if (assocLower.includes('recreation') || assocLower.includes('park')) {
    associationTypeKey = 'recreation_parks';
  } else if (cost.toLowerCase() === 'free' || assocLower.includes('life. money')) {
    associationTypeKey = 'free_subscription';
  }

  let confidence = 'Medium';
  if (notesLower.includes('excellent') || notesLower.includes('great')
    || notesLower.includes('high-limit') || notesLower.includes('100%')
    || name.includes('DCU') || name.includes('Alliant') || name.includes('BCU')) {
    confidence = 'High';
  }
  const confidenceKey = mapKey(CONFIDENCE_KEYS, confidence, 'medium');
  const recommended = confidenceKey === 'high' || cost.toLowerCase() === 'free' || cost.includes('$5');

  let bestForKey = 'general_membership';
  if (notesLower.includes('credit builder')) bestForKey = 'credit_building';
  else if (notesLower.includes('business')) bestForKey = 'business_accounts';
  else if (notesLower.includes('high-limit')) bestForKey = 'high_limit_products';

  const whyItMattersKey = 'association_access';
  const guidanceKeys = ['join_during_app', 'association_onetime', 'verify_pricing'];

  return {
    id: rowKey,
    name,
    association,
    cost: cost || label(t, CU, 'labels.varies', 'Varies'),
    notes,
    confidenceKey,
    confidenceLabel: labelConfidence(t, CU, confidenceKey),
    recommended,
    associationTypeKey,
    associationTypeLabel: labelAssocType(t, CU, associationTypeKey),
    bestForKey,
    bestForLabel: labelBestFor(t, CU, bestForKey),
    whyItMattersKey,
    whyItMattersLabel: labelWhy(t, CU, whyItMattersKey),
    guidanceKeys,
    guidanceLabels: guidanceKeys.map((k) => labelGuidance(t, CU, k)),
  };
};
